import React, { useState, useEffect, useMemo } from 'react';
import { TruckIcon, ArrowPathIcon, MagnifyingGlassIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { useXmlCtes, useXmlCteStats } from '../hooks/useCte';
import type { XmlCteFilters, ConsultanteOpcao } from '../types/cte';
import CteList from '../components/cte/CteList';
import CteModal from '../components/cte/CteModal';
import type { XmlCte } from '../types/cte';
import cteXmlApi from '../api/cte';
import { money, formatCnpj } from '../utils/format';
import { inputIris } from '../components/ui/iris';

/** Período pré-definido (dias) — chip de seleção única. */
const PERIODOS = [
  { label: 'Hoje', value: 1 },
  { label: '7 dias', value: 7 },
  { label: '15 dias', value: 15 },
  { label: '30 dias', value: 30 },
  { label: '60 dias', value: 60 },
  { label: '90 dias', value: 90 },
];

/** KPI Íris (desktop): eyebrow maiúsculo + número grande tabular. Sem sombra, sem
 *  ícone-em-círculo — mantém o padrão das outras telas (Reposição etc). */
const Kpi: React.FC<{ label: string; value: React.ReactNode; valueClass?: string; small?: boolean }> = ({
  label, value, valueClass = 'text-tx', small,
}) => (
  <div className="flex flex-col justify-center rounded-iris border border-line bg-surface px-3.5 py-2.5">
    <span className="text-[10px] font-medium uppercase tracking-wide text-tx-3">{label}</span>
    <span className={`mt-0.5 tabular-nums ${small ? 'text-sm font-semibold' : 'text-2xl font-bold'} ${valueClass}`}>{value}</span>
  </div>
);

const CteMonitoringPage: React.FC = () => {
  const [filters, setFilters] = useState<XmlCteFilters>({ dias: 30 });
  const [selectedCte, setSelectedCte] = useState<XmlCte | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [searchTerm, setSearchTerm] = useState('');
  // Multi-CNPJ: opções do filtro "Consultante" (só aparece com 2+ cadastrados)
  const [consultantes, setConsultantes] = useState<ConsultanteOpcao[]>([]);

  useEffect(() => {
    cteXmlApi.consultantesOpcoes()
      .then(setConsultantes)
      .catch(() => setConsultantes([]));
  }, []);

  const activeFilters = useMemo(() => ({ ...filters, search: searchTerm || undefined }), [filters, searchTerm]);

  const { data: response, isLoading, error, refetch } = useXmlCtes(activeFilters, currentPage, itemsPerPage);
  const { data: stats } = useXmlCteStats(activeFilters);

  const xmlCtes = response?.data.data || [];
  const totalPages = response?.data.pagination?.totalPages || 1;
  const totalItems = response?.data.pagination?.totalItems || 0;
  const startIndex = ((response?.data.pagination?.page || 1) - 1) * (response?.data.pagination?.pageSize || itemsPerPage);

  useEffect(() => { setCurrentPage(1); }, [filters, searchTerm, itemsPerPage]);

  const handleOpenModal = (cte: XmlCte) => { setSelectedCte(cte); setIsModalOpen(true); };
  const handleCloseModal = () => { setSelectedCte(null); setIsModalOpen(false); };

  const usandoPeriodoCustom = filters.dias === null || !!filters.dataInicio || !!filters.dataFim;

  const selecionarPeriodo = (dias: number) => {
    setFilters(prev => ({ ...prev, dias, dataInicio: undefined, dataFim: undefined }));
    setCurrentPage(1);
  };

  const abrirPeriodoCustom = () => {
    const hoje = new Date();
    const dataFimDefault = hoje.toISOString().split('T')[0];
    const inicio = new Date();
    inicio.setDate(inicio.getDate() - 30);
    setFilters(prev => ({
      ...prev,
      dias: null,
      dataInicio: prev.dataInicio || inicio.toISOString().split('T')[0],
      dataFim: prev.dataFim || dataFimDefault,
    }));
    setCurrentPage(1);
  };

  // Chip de período: selecionado pinta com o accent (seleção de valor, não toggle).
  const chip = (ativo: boolean) =>
    `h-9 rounded-iris px-3 text-sm font-medium transition-colors ${
      ativo ? 'bg-accent text-white' : 'bg-surface-2 text-tx-2 hover:bg-surface'
    }`;

  if (error) {
    return (
      <div className="mx-auto max-w-[1280px] space-y-4">
        <div className="rounded-iris-lg border border-danger-border bg-danger-soft p-4">
          <h3 className="text-sm font-semibold text-danger">Erro ao carregar CT-e</h3>
          <p className="mt-1 text-sm text-tx-2">Não foi possível carregar os conhecimentos de transporte. Tente atualizar.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1280px] space-y-4">
      {/* Header canônico Íris — refresh fica SEMPRE no topo à direita */}
      <header className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <h1 className="flex items-center gap-2 text-xl font-semibold tracking-[-0.01em] text-tx">
            <TruckIcon className="h-6 w-6 flex-shrink-0 text-accent-text" />
            CT-e de Terceiros
          </h1>
          <p className="mt-0.5 text-sm text-tx-3">Conhecimentos de transporte eletrônicos baixados da SEFAZ.</p>
        </div>
        <button
          onClick={() => refetch()}
          disabled={isLoading}
          className="inline-flex h-9 flex-shrink-0 items-center gap-1.5 rounded-iris px-2.5 text-sm font-medium text-tx-2 transition-colors hover:bg-surface-2 disabled:opacity-50"
          title="Atualizar lista"
        >
          <ArrowPathIcon className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          <span className="hidden sm:inline">Atualizar</span>
        </button>
      </header>

      {/* KPIs — cards no desktop (padrão das outras telas); mini-tira no mobile */}
      <div className="hidden gap-2.5 sm:grid sm:grid-cols-3">
        <Kpi label="Total de CT-e" value={stats?.total ?? 0} />
        <Kpi label="Valor total" value={money(stats?.valorTotal)} valueClass="text-ok" />
        <Kpi
          label="Última atualização"
          small
          value={
            stats?.ultimaAtualizacao
              ? new Date(stats.ultimaAtualizacao).toLocaleString('pt-BR', {
                  day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
                })
              : '—'
          }
        />
      </div>
      {/* Mini-KPI (mobile): uma tira compacta — total · valor · atualizado */}
      <div className="flex items-baseline gap-x-4 rounded-iris border border-line bg-surface px-3.5 py-2 sm:hidden">
        <span className="text-tx">
          <span className="text-base font-bold tabular-nums">{stats?.total ?? 0}</span>
          <span className="ml-1 text-[10px] uppercase tracking-wide text-tx-3">CT-e</span>
        </span>
        <span className="text-tx">
          <span className="text-[10px] uppercase tracking-wide text-tx-3">Valor </span>
          <span className="text-base font-bold tabular-nums text-ok">{money(stats?.valorTotal)}</span>
        </span>
        {stats?.ultimaAtualizacao && (
          <span className="ml-auto text-[10px] tabular-nums text-tx-3">
            {new Date(stats.ultimaAtualizacao).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
          </span>
        )}
      </div>

      {/* Deck de controle: busca + período */}
      <div className="rounded-iris-lg border border-line bg-surface p-3 sm:p-4">
        <div className="flex flex-col gap-3">
          <div className="relative">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-tx-3" />
            <input
              type="text"
              placeholder="Buscar por emitente, remetente, destinatário, chave ou número…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`${inputIris} w-full pl-9`}
            />
          </div>

          {/* Chips de período — rolagem horizontal no mobile (não quebra), wrap no desktop */}
          <div className="-mx-3 flex gap-2 overflow-x-auto px-3 pb-0.5 sm:mx-0 sm:flex-wrap sm:overflow-visible sm:px-0 sm:pb-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {PERIODOS.map((p) => (
              <button
                key={p.value}
                onClick={() => selecionarPeriodo(p.value)}
                className={`flex-shrink-0 ${chip(filters.dias === p.value && !usandoPeriodoCustom)}`}
              >
                {p.label}
              </button>
            ))}
            <button onClick={abrirPeriodoCustom} className={`flex-shrink-0 ${chip(usandoPeriodoCustom)}`}>
              Personalizado
            </button>
          </div>

          {/* Consultante (condicional) + Limpar — linha própria; Limpar como ghost, sem órfão pesado */}
          <div className="flex items-center gap-2">
            {consultantes.length > 1 && (
              <select
                value={filters.consultante ?? ''}
                onChange={(e) => { setFilters(prev => ({ ...prev, consultante: e.target.value || undefined })); setCurrentPage(1); }}
                title="Filtrar pelo consultante que baixou o CT-e"
                className={`${inputIris} min-w-0 flex-1 sm:flex-none`}
              >
                <option value="">Todos os consultantes</option>
                {consultantes.map(c => (
                  <option key={c.id} value={c.cnpj}>
                    {String(c.id).padStart(2, '0')} — {formatCnpj(c.cnpj)}
                  </option>
                ))}
              </select>
            )}

            <button
              onClick={() => { setSearchTerm(''); setFilters({ dias: 30 }); setCurrentPage(1); }}
              className="ml-auto inline-flex h-9 items-center rounded-iris px-3 text-sm font-medium text-tx-2 transition-colors hover:bg-surface-2"
            >
              Limpar
            </button>
          </div>

          {/* Intervalo personalizado */}
          {usandoPeriodoCustom && (
            <div className="flex flex-wrap items-center gap-3 rounded-iris bg-surface-2 p-3">
              <label className="flex items-center gap-2 text-sm text-tx-2">
                De:
                <input
                  type="date"
                  value={filters.dataInicio || ''}
                  onChange={(e) => { setFilters(prev => ({ ...prev, dataInicio: e.target.value || undefined, dias: null })); setCurrentPage(1); }}
                  className={inputIris}
                />
              </label>
              <label className="flex items-center gap-2 text-sm text-tx-2">
                Até:
                <input
                  type="date"
                  value={filters.dataFim || ''}
                  onChange={(e) => { setFilters(prev => ({ ...prev, dataFim: e.target.value || undefined, dias: null })); setCurrentPage(1); }}
                  className={inputIris}
                />
              </label>
              <button
                onClick={() => { setFilters(prev => ({ ...prev, dataInicio: undefined, dataFim: undefined, dias: 30 })); setCurrentPage(1); }}
                className="text-sm text-tx-3 underline hover:text-tx-2"
              >
                Voltar pra 30 dias
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Lista */}
      <CteList xmlCtes={xmlCtes} onSelect={handleOpenModal} isLoading={isLoading} />

      {/* Paginação */}
      {totalItems > 0 && (
        <div className="flex flex-col items-center justify-between gap-3 rounded-iris-lg border border-line bg-surface px-3 py-2.5 sm:flex-row">
          <div className="text-[13px] text-tx-2">
            <span className="font-medium text-tx">{startIndex + 1}</span>–
            <span className="font-medium text-tx">{Math.min(startIndex + itemsPerPage, totalItems)}</span> de{' '}
            <span className="font-medium text-tx">{totalItems}</span>
          </div>

          <div className="flex items-center gap-3">
            <label className="flex items-center gap-1.5 text-[13px] text-tx-2">
              Por página:
              <select
                value={itemsPerPage}
                onChange={(e) => setItemsPerPage(Number(e.target.value))}
                className={inputIris}
              >
                {[25, 50, 100, 200].map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </label>

            {totalPages > 1 && (
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-iris text-tx-2 transition-colors hover:bg-surface-2 disabled:opacity-40"
                  title="Página anterior"
                >
                  <ChevronLeftIcon className="h-4 w-4" />
                </button>
                <span className="min-w-[64px] text-center text-[13px] font-medium tabular-nums text-tx">
                  {currentPage} / {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-iris text-tx-2 transition-colors hover:bg-surface-2 disabled:opacity-40"
                  title="Próxima página"
                >
                  <ChevronRightIcon className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {selectedCte && <CteModal cte={selectedCte} isOpen={isModalOpen} onClose={handleCloseModal} />}
    </div>
  );
};

export default CteMonitoringPage;
